import { Component, Input, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    DateFormatService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    OrganisationService,
    PersonsOrganisationDto,
    UpdateOrganisationUnitsPreferencesCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    OrganisationFormSettings,
    dateFormatOptions,
    distanceExamples,
    distanceFormatOptions,
    findFormatLabelFromValue,
    formatCurrentMoment,
    heightExamples,
    heightFormatOptions,
    speedExamples,
    speedFormatOptions,
    temperatureExamples,
    temperatureFormatOptions,
    timeFormatOptions,
    weightExamples,
    weightFormatOptions
} from './helpers';

@Component({
    selector: 'organisation-units-preferences',
    templateUrl: './organisation-units-preferences.component.html',
    styleUrls: ['./organisation-units-preferences.component.scss']
})
export class OrganisationUnitsPreferencesComponent {
    @Input() organisation: PersonsOrganisationDto;
    @Input() canEdit = false;

    organisationSettingsForm: FormGroup<OrganisationFormSettings>;

    private ngUnsubscribe$ = new Subject<void>();
    private workTracker = new WorkTracker();
    working = false;

    // Options
    dateFormatOptions = dateFormatOptions;
    timeFormatOptions = timeFormatOptions;
    distanceFormatOptions = distanceFormatOptions;
    heightFormatOptions = heightFormatOptions;
    speedFormatOptions = speedFormatOptions;
    temperatureFormatOptions = temperatureFormatOptions;
    weightFormatOptions = weightFormatOptions;

    constructor(
        private organisationService: OrganisationService,
        private logging: FlyFreelyLoggingService,
        private dateFormatService: DateFormatService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.organisationSettingsForm = new FormGroup<OrganisationFormSettings>(
            {
                dateFormat: new FormControl(undefined, Validators.required),
                dateTimeFormat: new FormControl(undefined, Validators.required),
                distanceUnit: new FormControl(undefined, Validators.required),
                heightUnit: new FormControl(undefined, Validators.required),
                speedUnit: new FormControl(undefined, Validators.required),
                temperatureUnit: new FormControl(
                    undefined,
                    Validators.required
                ),
                timeFormat: new FormControl(undefined, Validators.required),
                weightUnit: new FormControl(undefined, Validators.required)
            }
        );

        this.organisationSettingsForm.controls.dateFormat.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.updateDateTimeFormat());

        this.organisationSettingsForm.controls.timeFormat.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.updateDateTimeFormat());
    }

    ngOnInit() {
        this.updateSettingsForm();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('organisation' in changes) {
            this.updateSettingsForm();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private updateDateTimeFormat() {
        const dateFormat =
            this.organisationSettingsForm.controls.dateFormat.value;
        const timeFormat =
            this.organisationSettingsForm.controls.timeFormat.value;

        if (dateFormat && timeFormat) {
            const dateTimeFormat = `${dateFormat} ${timeFormat}`;
            this.organisationSettingsForm.controls.dateTimeFormat.setValue(
                dateTimeFormat,
                { emitEvent: false }
            );
        }
    }

    updateSettingsForm() {
        const organisationPreferences = this.organisation?.unitsPreferences;
        this.organisationSettingsForm.patchValue({
            dateFormat: organisationPreferences?.dateFormat ?? 'DD/MM/YYYY',
            dateTimeFormat:
                organisationPreferences?.dateTimeFormat ??
                'DD MMM YYYY hh:mm a',
            distanceUnit: organisationPreferences?.distanceUnit ?? 'METRE',
            heightUnit: organisationPreferences?.heightUnit ?? 'FEET',

            speedUnit:
                organisationPreferences?.speedUnit ?? 'METRES_PER_SECOND',

            temperatureUnit:
                organisationPreferences?.temperatureUnit ?? 'CELSIUS',

            timeFormat: organisationPreferences?.timeFormat ?? 'HH:mm',

            weightUnit: organisationPreferences?.weightUnit ?? 'GRAM'
        });
    }

    updateUnitsPreferences = () => {
        if (this.organisationSettingsForm.invalid) {
            this.logging.error(
                null,
                'The current units preferences are invalid, please ensure correct values are selected.'
            );
            return Promise.reject();
        }
        const command: UpdateOrganisationUnitsPreferencesCommand = this
            .organisationSettingsForm
            .value as UpdateOrganisationUnitsPreferencesCommand;

        const previousOrganisation = this.organisation;
        this.organisation = {
            ...this.organisation,
            unitsPreferences: {
                ...this.organisation.unitsPreferences,
                ...command
            }
        };

        this.organisationService
            .updateOrganisationUnitsPreferences(this.organisation.id, command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.logging.success('Units preferences updated');
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating organisation units preferences: ${error.message}`
                    );
                    this.organisation = previousOrganisation;
                    this.updateSettingsForm();
                }
            })
            .add(this.workTracker.createTracker());
    };

    revertSettingsChange(formControlname: string) {
        this.organisationSettingsForm.controls[formControlname].patchValue(
            this.organisation.unitsPreferences[formControlname]
        );
        this.organisationSettingsForm.updateValueAndValidity();
    }

    // Date formatters
    get formattedDateLabel() {
        return findFormatLabelFromValue(
            this.dateFormatOptions,
            this.organisation.unitsPreferences?.dateFormat
        );
    }

    get formattedDateExample() {
        return formatCurrentMoment(
            this.organisation.unitsPreferences?.dateFormat
        );
    }

    // Time formatters
    get formattedTimeLabel() {
        return findFormatLabelFromValue(
            this.timeFormatOptions,
            this.organisation.unitsPreferences?.timeFormat
        );
    }

    get formattedTimeExample() {
        return formatCurrentMoment(
            this.organisation.unitsPreferences?.timeFormat
        );
    }

    // Height formatters
    get formattedHeightLabel() {
        return findFormatLabelFromValue(
            this.heightFormatOptions,
            this.organisation.unitsPreferences?.heightUnit
        );
    }

    get formattedHeightExample() {
        return findFormatLabelFromValue(
            heightExamples,
            this.organisation.unitsPreferences?.heightUnit
        );
    }

    // Distance formatters
    get formattedDistanceLabel() {
        return findFormatLabelFromValue(
            this.distanceFormatOptions,
            this.organisation.unitsPreferences?.distanceUnit
        );
    }

    get formattedDistanceExample() {
        return findFormatLabelFromValue(
            distanceExamples,
            this.organisation.unitsPreferences?.distanceUnit
        );
    }

    // Speed formatters
    get formattedSpeedLabel() {
        return findFormatLabelFromValue(
            this.speedFormatOptions,
            this.organisation.unitsPreferences?.speedUnit
        );
    }

    get formattedSpeedExample() {
        return findFormatLabelFromValue(
            speedExamples,
            this.organisation.unitsPreferences?.speedUnit
        );
    }

    // Temperature formatters
    get formattedTemperatureLabel() {
        return findFormatLabelFromValue(
            this.temperatureFormatOptions,
            this.organisation.unitsPreferences?.temperatureUnit
        );
    }

    get formattedTemperatureExample() {
        return findFormatLabelFromValue(
            temperatureExamples,
            this.organisation.unitsPreferences?.temperatureUnit
        );
    }

    // Weight formatters
    get formattedWeightLabel() {
        return findFormatLabelFromValue(
            this.weightFormatOptions,
            this.organisation.unitsPreferences?.weightUnit
        );
    }

    get formattedWeightExample() {
        return findFormatLabelFromValue(
            weightExamples,
            this.organisation.unitsPreferences?.weightUnit
        );
    }
}
